import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";
import { useAuth } from "@/context/AuthContext";
import { Users, Play, Copy, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LobbyRoom() {
  const { lobbyId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [lobby, setLobby] = useState(null);
  const [copied, setCopied] = useState(false);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/lobbies/${lobbyId}`);
      setLobby(res.data);
      if (res.data.status === "active") {
        navigate(`/race/${lobbyId}`);
      }
    } catch (e) {
      toast.error("Lobby not found");
      navigate("/lobbies");
    }
  };

  useEffect(() => {
    // Auto-join on entry
    axios
      .post(`${BACKEND_URL}/api/lobbies/${lobbyId}/join`, {}, { withCredentials: true })
      .catch(() => {});
    load();
    pollRef.current = setInterval(load, 2500);
    return () => clearInterval(pollRef.current);
  }, [lobbyId]); // eslint-disable-line

  const startRace = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/lobbies/${lobbyId}/start`, {}, { withCredentials: true });
      navigate(`/race/${lobbyId}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start");
    }
  };

  const copyInvite = async () => {
    const link = `${window.location.origin}/lobbies/${lobbyId}`;
    await navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success("Invite link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  if (!lobby) return <AppLayout><div className="p-10 label-kicker">Loading lobby…</div></AppLayout>;

  const isHost = lobby.host_id === user?.user_id;

  return (
    <AppLayout>
      <div className="px-6 lg:px-12 py-10 max-w-4xl">
        <div className="label-kicker">Race lobby</div>
        <h1 className="font-heading text-5xl lg:text-6xl mt-1">{lobby.city_name}</h1>
        <div className="mt-3 text-white/50 text-sm">
          Waiting for the host to drop the flag. Share the lobby link to get more racers in.
        </div>

        <div className="mt-8 flex flex-wrap gap-3 items-center">
          <button
            className="btn-ghost flex items-center gap-2"
            onClick={copyInvite}
            data-testid="lobby-copy-invite"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? "Copied" : "Copy invite"}
          </button>
          {isHost && (
            <button
              className="btn-primary flex items-center gap-2"
              onClick={startRace}
              data-testid="lobby-start-race"
            >
              <Play className="w-4 h-4" /> Start Race
            </button>
          )}
          {!isHost && (
            <div className="font-mono text-xs text-white/40 tracking-widest uppercase">
              Waiting for host to start…
            </div>
          )}
        </div>

        <div className="mt-10 surface-solid p-6">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-4 h-4 text-[#FF3B30]" />
            <div className="label-kicker">Racers in lobby · {lobby.players.length}</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {lobby.players.map((p) => (
              <div
                key={p.user_id}
                data-testid={`lobby-player-${p.user_id}`}
                className="flex items-center gap-3 border border-white/10 p-3"
              >
                {p.picture ? (
                  <img src={p.picture} alt={p.name} className="w-10 h-10 rounded-full border border-white/15" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[#FF3B30]/60 flex items-center justify-center text-black font-bold">
                    {p.name?.[0]?.toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-heading text-lg truncate">{p.name}</div>
                  {p.user_id === lobby.host_id && (
                    <div className="text-[10px] tracking-widest text-[#FFCC00] uppercase">Host</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
